import { ChartData, ChartDataset, ChartType } from "chart.js";
import moment from "moment";
import "moment/locale/de";
import "moment/locale/es";
import "moment/locale/fr";
import "moment/locale/ja";
import "moment/locale/ko";
import "moment/locale/pt";
import "moment/locale/zh-cn";
import React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import BarChartWidget from "./BarChartWidget";
import widgetStyle from "./chartWidget.scss";
import DoughnutChartWidget from "./DoughnutChartWidget";
import LineChartWidget from "./LineChartWidget";
import style from "./overviewDashboard.scss";
import SingleValueWidget from "./SingleValueWidget";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Tooltip from "components/tooltip/Tooltip";
import { MAX_DOUGHNUT_TERM_COUNT } from "domain/dashboard";
import { DataAvailability, DataSet, Widget } from "domain/overviewDashboard";
import { overviewDashboardService } from "services/dashboard/OverviewDashboardService";
import { getLanguage } from "services/language/languageRepository";
import { StoreState } from "store";
import { logger } from "utils/logging";

import testIds from "testIds.json";

const connector = connect((state: StoreState) => ({
    theme: state.themeReducer.theme,
}));

interface ChartProps {
    widget: Widget;
}

interface Legend {
    value: string;
    color: string;
}

type Props = ChartProps & ConnectedProps<typeof connector>;

export const DATE_LABEL_FORMAT: string = "D MMM YYYY";

/**
 * Formats an array of string labels for dashboard chart legends
 *
 * - Valid date strings are formatted using {@link DATE_LABEL_FORMAT}
 * - Non-date values (e.g., "50", "Successful") are returned unchanged
 * - UTC dates are preserved without local timezone conversion
 */
export const getFormattedLegends = (labels: string[]): string[] => {
    if (typeof labels === "undefined") {
        return [];
    }
    return labels.map((label) => {
        const lang = getLanguage().code;
        const labelToFormat = moment.utc(label, true).locale(lang);
        if (labelToFormat.isValid()) {
            return labelToFormat.format(DATE_LABEL_FORMAT);
        }

        return label;
    });
};

const ChartWidget = (props: Props): JSX.Element => {
    const { t } = useTranslation();
    const [lineChartData, setLineChartData] = React.useState<ChartData<"line">>({
        labels: [],
        datasets: [],
    });
    const [barChartData, setBarChartData] = React.useState<ChartData<"bar">>({
        labels: [],
        datasets: [],
    });
    const [doughnutChartData, setDoughnutChartData] = React.useState<ChartData<"doughnut">>({
        labels: [],
        datasets: [],
    });
    const [singleValueWidgetData, setSingleValueWidgetData] = React.useState<number | undefined>();
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState("");
    const lastResultRef = React.useRef<{ dataSets: DataSet[]; labels: string[] } | null>(null);
    const retryWaitDelays = [500, 5000, 300_000];

    const resolveColor = (cssVarName: string): string => {
        return getComputedStyle(document.body).getPropertyValue(cssVarName).trim();
    };

    const resolveAllColors = () => ({
        chartFirstSet: resolveColor("--app-chart-chartFirstSet"),
        chartSecondSet: resolveColor("--app-chart-chartSecondSet"),
        chartThirdSet: resolveColor("--app-chart-chartThirdSet"),
        chartForthSet: resolveColor("--app-chart-chartForthSet"),
        chartFifthSet: resolveColor("--app-chart-chartFifthSet"),
        chartSixthSet: resolveColor("--app-chart-chartSixthSet"),
        chartSeventhSet: resolveColor("--app-chart-chartSeventhSet"),
        chartEighthSet: resolveColor("--app-chart-chartEighthSet"),
        chartNinthSet: resolveColor("--app-chart-chartNinthSet"),
        chartTenthSet: resolveColor("--app-chart-chartTenthSet"),
        chartEleventhSet: resolveColor("--app-chart-chartEleventhSet"),
        chartTwelfthSet: resolveColor("--app-chart-chartTwelfthSet"),
        chartSuccessColor: resolveColor("--app-chart-chartSuccessColor"),
        chartFailColor: resolveColor("--app-chart-chartFailColor"),
        chartNeutralColor: resolveColor("--app-chart-chartNeutralColor"),
    });

    const [resolvedColors, setResolvedColors] = React.useState(resolveAllColors);

    // Watch for theme changes to update colors embedded in chart data objects.
    React.useEffect(() => {
        const updateColors = () => {
            setResolvedColors(resolveAllColors());
        };

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === "attributes" && mutation.attributeName === "class") {
                    const classList = (mutation.target as HTMLElement).classList;
                    if (classList.contains("defaultTheme") || classList.contains("defaultDarkTheme")) {
                        updateColors();
                    }
                }
            });
        });

        observer.observe(document.body, { attributes: true });

        return () => observer.disconnect();
    }, []);

    const chartFirstSetColor = resolvedColors.chartFirstSet;
    const chartSuccessColor = resolvedColors.chartSuccessColor;
    const chartFailColor = resolvedColors.chartFailColor;
    const chartNeutralColor = resolvedColors.chartNeutralColor;

    const colors: string[] = [
        resolvedColors.chartFirstSet,
        resolvedColors.chartSecondSet,
        resolvedColors.chartThirdSet,
        resolvedColors.chartForthSet,
        resolvedColors.chartFifthSet,
        resolvedColors.chartSixthSet,
        resolvedColors.chartSeventhSet,
        resolvedColors.chartEighthSet,
        resolvedColors.chartNinthSet,
        resolvedColors.chartTenthSet,
        resolvedColors.chartEleventhSet,
        resolvedColors.chartTwelfthSet,
        resolvedColors.chartSuccessColor,
        resolvedColors.chartFailColor,
    ];

    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [legendLabels, setLegendLabels] = React.useState<Legend[]>();

    function waitFor(milliseconds: number) {
        return new Promise((resolve) => setTimeout(resolve, milliseconds));
    }

    function fetchAggregatedData(retries = 0) {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        overviewDashboardService
            .fetchAggregatedData(props.widget.aggregationQuery, abortController)
            .then(async (result) => {
                if (DataAvailability.PENDING == result.dataAvailability && retries < retryWaitDelays.length) {
                    await waitFor(retryWaitDelays[retries]);
                    await fetchAggregatedData(retries + 1);
                } else {
                    lastResultRef.current = { dataSets: result.dataSets, labels: result.labels };
                    if (props.widget.type == "SINGLE_VALUE_CHART") {
                        if (props.widget.aggregationQuery.type === "TIME_AGGREGATED_DOCUMENT_COUNT") {
                            setSingleValueWidgetData(result.dataSets[0]?.values[0]);
                        }
                    } else if (props.widget.type == "DOUGHNUT_CHART") {
                        setDoughnutChartData(getDoughnutChartData(result.dataSets[0], result.labels));
                    } else if (props.widget.type == "LINE_CHART") {
                        setLineChartData(getChartData(result.dataSets, result.labels, "line"));
                    } else {
                        setBarChartData(getChartData(result.dataSets, result.labels, "bar"));
                    }
                    setLegendLabels(computeLegendLabels(result.dataSets));
                }

                setLoading(false);
            })
            .catch((e) => {
                logger.error(e);
                setError(t("Overview.widgetErrorMessage"));
                setLoading(false);
            });
    }

    React.useEffect(() => {
        fetchAggregatedData();
        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
            retryWaitDelays.forEach((each) => clearTimeout(each));
        };
    }, []);

    React.useEffect(() => {
        if (lastResultRef.current) {
            const { dataSets, labels } = lastResultRef.current;
            if (props.widget.type == "DOUGHNUT_CHART") {
                setDoughnutChartData(getDoughnutChartData(dataSets[0], labels));
            } else if (props.widget.type == "LINE_CHART") {
                setLineChartData(getChartData(dataSets, labels, "line"));
            } else if (props.widget.type == "BAR_CHART") {
                setBarChartData(getChartData(dataSets, labels, "bar"));
            }
            setLegendLabels(computeLegendLabels(dataSets));
        }
    }, [resolvedColors]);

    const getChartData = <T extends "bar" | "line">(
        dataSets: DataSet[],
        labels: string[],
        chartType: ChartType
    ): ChartData<T> => {
        const formattedLabels = getFormattedLegends(labels);
        const dataLabels: string[] = [];
        //when formattedLabels has values Successful / failed
        const labelValuesToCheck: string[] = ["successful", "passed", "failed"];

        dataSets.forEach((dataSet) => {
            if (dataSet.matcher.value !== undefined) {
                dataLabels.push(dataSet.matcher.value);
            }
        });
        switch (props.widget.aggregationQuery.type) {
            case "TIME_AGGREGATED_DOCUMENT_COUNT":
                return {
                    datasets: dataLabels.map((label, index) => {
                        let applyColorsToChart: string[] = [];
                        const chartLabel = dataSets[index].matcher.value;

                        // possible values "Successful", "failed", "cancelled", "Completed with exceptions"
                        //"2022-01-01T00:00:00Z", 11,50,.....
                        applyColorsToChart = [];
                        formattedLabels.forEach(() => {
                            if (
                                typeof chartLabel == "string" &&
                                labelValuesToCheck.includes(chartLabel.toLowerCase())
                            ) {
                                applyColorsToChart.push(applyChartColors(chartLabel.toLowerCase()));
                            } else {
                                applyColorsToChart.push(colors[index]);
                            }
                        });

                        const baseDataset = {
                            label: label,
                            backgroundColor: applyColorsToChart, //keep existing colors value if there is no success/fail
                            data: dataSets[index].values,
                            borderColor: colors[index],
                        };

                        if (chartType === "line") {
                            return {
                                ...baseDataset,
                                fill: false,
                            } as ChartDataset<"line">;
                        }

                        return {
                            ...baseDataset,
                        } as ChartDataset<"bar">;
                    }),
                    labels: formattedLabels,
                } as ChartData<T>;
            case "TERM_AGGREGATED_DOCUMENT_COUNT":
                return {
                    datasets: dataSets.map((set, index) => {
                        const applyColorsToChart: string[] = [];
                        formattedLabels.forEach((value: string) => {
                            if (labelValuesToCheck.includes(value.toLowerCase())) {
                                applyColorsToChart.push(applyChartColors(value));
                            } else {
                                applyColorsToChart.push(colors[index]);
                            }
                        });

                        const baseDataset = {
                            backgroundColor: applyColorsToChart,
                            data: set.values,
                            borderColor: colors[index],
                        };

                        if (chartType === "line") {
                            return {
                                ...baseDataset,
                                fill: false,
                            } as ChartDataset<"line">;
                        }

                        return {
                            ...baseDataset,
                        } as ChartDataset<"bar">;
                    }),
                    labels: formattedLabels,
                } as ChartData<T>;
        }
    };

    const getDoughnutChartData = (
        dataSet: DataSet | undefined,
        labelsParam: string[] | undefined
    ): ChartData<"doughnut"> => {
        if (dataSet === undefined || labelsParam === undefined) {
            return {
                labels: [t("Common.noDataToDisplay")],
                datasets: [{ backgroundColor: chartNeutralColor, data: [100] }],
            };
        }
        let labels = labelsParam;
        let legendValueMap = new Map();
        const labelValuesToCheck: string[] = ["successful", "passed", "failed"];
        for (let i = 0; i < dataSet.values.length; i++) {
            legendValueMap.set(labels[i], dataSet.values[i]);
        }
        legendValueMap = new Map([...legendValueMap.entries()].sort((a, b) => b[1] - a[1]));
        const maxTermCountWithoutOther = MAX_DOUGHNUT_TERM_COUNT - 1;
        const numberOfValues = Math.min(legendValueMap.size, maxTermCountWithoutOther);
        const reducer = (accumulator: number, current: number) => accumulator + current;
        const sumOfOthers = Array.from(legendValueMap.values())
            .slice(numberOfValues, legendValueMap.size)
            .reduce(reducer, 0);
        const finalLegendValueMap = new Map(Array.from(legendValueMap).slice(0, numberOfValues));
        if (legendValueMap.size > maxTermCountWithoutOther) {
            finalLegendValueMap.set(t("Overview.doughnutChartWidget.others"), sumOfOthers);
        }
        const isZero = (currentValue: number) => currentValue === 0;
        if (Array.from(finalLegendValueMap.values()).every(isZero)) {
            const label = t("Common.noDataToDisplay");
            return {
                labels: [label],
                datasets: [
                    {
                        backgroundColor: chartNeutralColor,
                        data: [100],
                    },
                ],
            };
        } else {
            labels = Array.from(finalLegendValueMap.keys());
            const applyColorsToChart: string[] = [];
            labels.forEach((value: string) => {
                if (labelValuesToCheck.includes(value.toLowerCase())) {
                    applyColorsToChart.push(applyChartColors(value));
                } else if (labels.includes("cancelled") || labels.includes("Cancelled")) {
                    applyColorsToChart.push(chartFirstSetColor);
                }
            });
            return {
                datasets: [
                    {
                        backgroundColor: applyColorsToChart.length == 0 ? colors : applyColorsToChart,
                        data: Array.from(finalLegendValueMap.values()),
                    },
                ],
                labels: getFormattedLegends(labels),
            };
        }
    };

    const applyChartColors = (label: string): string => {
        let returnColor = "";
        if (label.toLowerCase() === "successful" || label.toLowerCase() === "passed") {
            returnColor = chartSuccessColor;
        } else if (label.toLowerCase() == "failed") {
            returnColor = chartFailColor;
        }
        return returnColor;
    };

    const computeLegendLabels = (dataSets: DataSet[]): Legend[] => {
        const legends: Legend[] = [];
        dataSets.map((set, index: number) => {
            const legend = set.matcher.legend;
            if (typeof legend === "string" && legend.trim().length > 0) {
                legends.push({
                    value: legend,
                    color: colors[index],
                });
            }
        });
        return legends;
    };

    const legend = (
        <div className={widgetStyle.legend}>
            {legendLabels?.map((label, key) => (
                <div key={key} className={widgetStyle.label}>
                    <div className={widgetStyle.dot} style={{ backgroundColor: colors[key] }} />
                    <Tooltip content={label.value}>
                        <span className={widgetStyle.legendValue}>{label.value}</span>
                    </Tooltip>
                </div>
            ))}
        </div>
    );

    let chartComponent = <></>;
    switch (props.widget.type) {
        case "BAR_CHART":
            chartComponent = (
                <>
                    <BarChartWidget chartData={barChartData} />
                    {legend}
                </>
            );
            break;
        case "LINE_CHART":
            chartComponent = (
                <>
                    <LineChartWidget chartData={lineChartData} />
                    {legend}
                </>
            );
            break;
        case "DOUGHNUT_CHART":
            chartComponent = <DoughnutChartWidget chartData={doughnutChartData} />;
            break;
        case "SINGLE_VALUE_CHART":
            chartComponent = <SingleValueWidget value={singleValueWidgetData} />;
            break;
    }

    return (
        <div className={style.widgetContainer} data-testid={testIds.workArea.dashboard.overview.widgetSlot.itself}>
            {error !== "" ? (
                <div>{error}</div>
            ) : loading ? (
                <div className={style.loadingState}>
                    <LoadingIndicator />
                </div>
            ) : (
                <>{chartComponent}</>
            )}
        </div>
    );
};
export default connector(ChartWidget);
